import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { teamApi, tasksApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User, Mail, Shield, FolderOpen, ListTodo, ExternalLink, Ban, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const PROJECT_ROLES_LABEL = (slug) => (slug || "").split("_").map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
const ACCESS_LABEL = (slug) => ({ view: "View", contribute: "Contribute", manage_project: "Manage Project" })[slug] || PROJECT_ROLES_LABEL(slug);

/**
 * Unified Member Profile (consolidated build §2).
 *
 * One page that answers for Admin:
 *   - where does this person have access?
 *   - what are they assigned to?
 *   - what have they documented?
 *
 * Pulls:
 *   GET /team/users/{uid}           → user + memberships (project+entity names)
 *   GET /team/users/{uid}/tasks     → assigned tasks across all projects
 *
 * Every assigned-task row opens the dedicated `/task/:id` workspace —
 * NOT the global Tasks list.
 */
export default function MemberProfilePage() {
  const { uid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);

  const load = useCallback(async () => {
    try {
      const [u, t] = await Promise.all([teamApi.getUser(uid), teamApi.listUserTasks(uid)]);
      setData(u.data);
      setTasks(t.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load member");
    }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="h-full flex items-center justify-center text-white/60" data-testid="member-profile-loading">Loading member…</div>;
  }

  const user = data.user || {};
  const memberships = (data.memberships || []).filter(m => m.active);
  const inactiveMemberships = (data.memberships || []).filter(m => !m.active);
  const entityIds = Array.from(new Set(memberships.map(m => m.entity_id).filter(Boolean)));
  const entityMap = Object.fromEntries(memberships.map(m => [m.entity_id, m.entity_name]));
  const isDeleted = !!user.deleted_at || (user.email || "").startsWith("deleted+");

  const revoke = async (mid) => {
    if (!window.confirm("Revoke access to this project?")) return;
    try { await teamApi.removeMembership(mid); await load(); toast.success("Access revoked"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };
  const disable = async () => {
    if (!window.confirm("Disable this account?")) return;
    try { await teamApi.disableUser(uid); await load(); toast.success("Disabled"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Disable failed"); }
  };
  const enable = async () => {
    try { await teamApi.enableUser(uid); await load(); toast.success("Enabled"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enable failed"); }
  };
  const colour = (p) => p === "Critical" ? "text-red-300" : p === "High" ? "text-amber-300" : p === "Medium" ? "text-sky-300" : "text-white/60";

  return (
    <div className="h-full overflow-y-auto bg-[#0B0E14] text-white px-6 py-8" data-testid="member-profile-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-white/50 hover:text-white text-sm" data-testid="member-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* HEADER */}
        <div className="p-6 rounded-xl border border-white/10 bg-[#151A23]" data-testid="member-header">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40">Team Member</div>
              <div className="text-2xl font-display font-semibold mt-0.5 flex items-center gap-2">
                <User className="w-5 h-5 text-white/50" />
                {user.name || "—"}
                {isDeleted && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/15 bg-white/5 text-white/60">
                    Deleted
                  </span>
                )}
              </div>
              <div className="text-sm text-white/60 mt-1 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> {user.email || "—"}
              </div>
              <div className="text-xs text-white/50 mt-1 flex items-center gap-1.5">
                <Shield className="w-3 h-3" /> {user.platform_role || user.role || "member"}
                {user.disabled && !isDeleted && <span className="text-amber-300">· account disabled</span>}
              </div>
              {data.contact && (
                <div className="text-xs text-white/50 mt-1 flex items-center gap-1.5" data-testid="member-linked-contact">
                  <User className="w-3 h-3" />
                  Canonical contact:{" "}
                  <Link to={`/contacts/${data.contact.id}`} className="text-white/80 hover:underline inline-flex items-center gap-1"
                        data-testid="member-view-contact-link">
                    {data.contact.full_name || data.contact.email || "View Contact"} <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              )}
            </div>
            {!isDeleted && (
              <div className="flex gap-1">
                {user.disabled ? (
                  <Button size="sm" variant="ghost" onClick={enable} className="text-emerald-300 hover:bg-emerald-500/10" data-testid="member-enable">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Enable
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={disable} className="text-red-400 hover:bg-red-500/10" data-testid="member-disable">
                    <Ban className="w-4 h-4 mr-1" /> Disable
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* ENTITIES SUMMARY */}
          {entityIds.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="member-entities">
              <span className="text-[11px] uppercase tracking-wider text-white/40 mr-2 self-center">Entities</span>
              {entityIds.map(eid => (
                <Link key={eid} to={`/entity/${eid}`}
                      className="text-xs px-2 py-0.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10"
                      data-testid={`member-entity-${eid}`}>
                  {entityMap[eid] || "—"}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* MEMBERSHIPS */}
        <section className="p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="member-memberships-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Project Access</h3>
            <span className="text-xs text-white/50">{memberships.length} active</span>
          </div>
          {memberships.length === 0 && <div className="text-sm text-white/40 italic">No active project access.</div>}
          <div className="space-y-2">
            {memberships.map(m => (
              <div key={m.id}
                   className="p-3 rounded border border-white/10 bg-white/[0.02] flex items-center justify-between gap-2"
                   data-testid={`member-membership-${m.id}`}>
                <div className="min-w-0">
                  <Link to={`/entity/${m.entity_id}?tab=projects`} className="text-sm text-white/90 hover:underline">
                    {m.entity_name || "—"} <span className="text-white/40">→</span> {m.project_name || "—"}
                  </Link>
                  <div className="text-[11px] text-white/50 mt-0.5">
                    Role: <span className="text-white/80">{PROJECT_ROLES_LABEL(m.project_role) || "—"}</span>
                    {" · "}Access: <span className="text-white/80">{ACCESS_LABEL(m.access_level) || "Contribute"}</span>
                  </div>
                </div>
                {!isDeleted && (
                  <button onClick={() => revoke(m.id)}
                          className="text-red-400 hover:underline text-xs"
                          data-testid={`member-revoke-${m.id}`}>
                    Revoke Access
                  </button>
                )}
              </div>
            ))}
          </div>
          {inactiveMemberships.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5">Revoked history</div>
              <div className="space-y-1">
                {inactiveMemberships.map(m => (
                  <div key={m.id} className="text-[11px] text-white/50" data-testid={`member-revoked-${m.id}`}>
                    {m.entity_name || "—"} → {m.project_name || "—"} · {PROJECT_ROLES_LABEL(m.project_role) || "—"} · revoked
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ASSIGNED TASKS */}
        <section className="p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="member-tasks-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg flex items-center gap-2"><ListTodo className="w-4 h-4" /> Assigned Tasks</h3>
            <span className="text-xs text-white/50">{tasks.length} total</span>
          </div>
          {tasks.length === 0 && <div className="text-sm text-white/40 italic">No tasks assigned yet.</div>}
          <div className="space-y-1.5">
            {tasks.map(t => (
              <Link key={t.id} to={`/task/${t.id}`}
                    className="flex items-center justify-between gap-3 p-2.5 rounded border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition"
                    data-testid={`member-task-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm ${t.status === "Done" ? "line-through text-white/40" : "text-white/90"}`}>{t.name}</div>
                  <div className="text-[11px] text-white/50 mt-0.5">
                    {t.project_name || "—"}{t.entity_name ? ` · ${t.entity_name}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-[11px]">
                  <span className="text-white/60">{t.status || "—"}</span>
                  <span className={colour(t.priority)}>{t.priority || "—"}</span>
                  <span className="text-white/50">{t.due_date || "—"}</span>
                  <ExternalLink className="w-3 h-3 text-white/40" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
